import React, { useState } from 'react';
import { Form, FormSpy } from 'react-final-form';
import { useSelector } from 'react-redux';
import { ApplePayConfig } from '../../../resources/CreditCard';
import { ICreditCard } from '../../../models/CreditCard';
import { InmateAccountPaymentData } from '../../../models/InmateAccountPayment';
import { IAppState } from '../../../reducers';
import ErrorMessage from '../../ErrorMessage';
import { ButtonSize, renderActionSubmit } from '../../Form/RenderForm';
import Currency from '../../Format/Currency';
import PaymentInformation from '../PaymentInformation';
import PersonalInformation from '../PersonalInformation';
import Agreement from './Agreement';
import {
  ApplePayPaymentResponse,
  processApplePayRequest
} from '../../../utils/applePay';

interface IPaymentProps {
  loggedIn: boolean
  amount: number
  fees: number
  taxes: number
  applePayConfig: ApplePayConfig|null
  acceptApplePay: boolean
  onSubmit: (data: InmateAccountPaymentData) => void
  savedCards: ICreditCard[]
  selectedSavedCard: ICreditCard
  onCreditCardSelect: (card: ICreditCard) => void
  onAddCreditCard: () => void
  addingCreditCard: boolean
  loadingSavedCreditCards: boolean
  onZipCodeChanged: (zip: string) => void
  onMobilePaymentResponse: (paymentResponse: ApplePayPaymentResponse) => void
}

const Payment: React.FC<IPaymentProps> = props => {
  const state = useSelector((appState: IAppState) => ({
    wallet: appState.checkout.wallet,
    auth: appState.auth,
    checkout: appState.checkout
  }));

  const [lastZip, setLastZip] = useState(null);
  const onFormChange = (values: { cc?: { zip?: string } }) => {
    if (values.cc && values.cc.zip && lastZip != values.cc.zip) {
      props.onZipCodeChanged(values.cc.zip);
      setLastZip(values.cc.zip);
    }
  };

  const hasPaymentMethod = props.selectedSavedCard != null
    || state.checkout.common.mobilePaymentRequest != null;

  return (
    <div className="inmatewallet service mainwindow ">
      <h3 className="product bg-action p-2 py-3 mb-0 text-white rounded-top border-action">
        <span className="icon-button" />
        Inmate Wallet - All Options: Checkout
      </h3>
      <div className="col-md-12 service-window px-0">
        <div className="maincontent p-0 m-0 pt-0 pt-md-0 px-4 border border-action rounded-bottom border-top-0">
          <Form
            mutators={{
              copyPersonalToBilling: (args, formState, tools) => {
                tools.changeValue(formState, 'cc.address', () => formState.lastFormState.values.address)
                tools.changeValue(formState, 'cc.city', () => formState.lastFormState.values.city)
                tools.changeValue(formState, 'cc.state', () => formState.lastFormState.values.state)
                tools.changeValue(formState, 'cc.zip', () => formState.lastFormState.values.zip)
              },
              useSavedCreditCard: (args, formState, tools) => {
                tools.changeValue(formState, 'cc', () => ({}))
              }
            }}
            initialValues={{
              ...state.wallet.paymentData,
              address: state.auth.user ? state.auth.user.address : '',
              city: state.auth.user ? state.auth.user.city : '',
              state: state.auth.user ? state.auth.user.state : '',
              zip: state.auth.user ? state.auth.user.zip : '',
              firstName: state.auth.user ? state.auth.user.firstName : '',
              lastName: state.auth.user ? state.auth.user.lastName : '',
              phoneNumber: state.auth.user ? state.auth.user.phoneNumber : '',
              receiptEmailAddress: state.auth.user ? state.auth.user.emailAddress : ''
            }}
            keepDirtyOnReinitialize={true}
            onSubmit={props.onSubmit}>
            {form =>
              <form className="pb-4" onSubmit={form.handleSubmit} method="post">
                <FormSpy
                  subscription={{ values: true }}
                  onChange={data => setTimeout(() => onFormChange(data.values))} />

                <PersonalInformation />

                <Agreement />

                <PaymentInformation
                  form={form}
                  loggedIn={state.auth.loggedIn}
                  acceptApplePay={props.acceptApplePay}
                  applePayConfig={props.applePayConfig}
                  applePayLineItems={{
                    amount: props.amount,
                    fees: props.fees,
                    taxes: props.taxes
                  }}
                  loadingSavedCreditCards={props.loadingSavedCreditCards}
                  savedCards={props.savedCards}
                  selectedSavedCard={props.selectedSavedCard}
                  onCreditCardSelect={props.onCreditCardSelect}
                  onAddCreditCard={props.onAddCreditCard}
                  addingCreditCard={props.addingCreditCard}
                  onMobilePaymentRequest={(paymentRequest) =>
                    processApplePayRequest(
                      paymentRequest,
                      form,
                      props.onMobilePaymentResponse,
                      'texting'
                    )
                  } />

                {!hasPaymentMethod && form.values.cc == null &&
                  <ErrorMessage message="Please supply a credit card for payment, if not using Apple Pay." />
                }

                {(hasPaymentMethod || form.values.cc != null) && !form.submitError && form.submitFailed && form.hasValidationErrors &&
                  <ErrorMessage message="The form has incomplete or invalid values." />
                }

                {renderActionSubmit(
                  form,
                  <strong>Pay <Currency value={props.amount + props.fees + props.taxes} /></strong>,
                  'Paying...',
                  ButtonSize.Large,
                  undefined,
                  undefined,
                  !hasPaymentMethod && form.values.cc == null
                )}
              </form>
            }
          </Form>
        </div>
      </div>
    </div>
  )
}

export default Payment
